/*
 * Application Insights JavaScript SDK - Common, 2.6.2
 * Copyright (c) Microsoft and contributors. All rights reserved.
 */

import { __extendsFn, __assignFn } from "@microsoft/applicationinsights-shims";
import { StackFrame } from '../Interfaces/Contracts/Generated/StackFrame';
import { ExceptionData } from '../Interfaces/Contracts/Generated/ExceptionData';
import { ExceptionDetails } from '../Interfaces/Contracts/Generated/ExceptionDetails';
import { DataSanitizer } from './Common/DataSanitizer';
import { FieldType } from '../Enums';
import { isNullOrUndefined, arrMap, isString, strTrim, isArray, isError } from '@microsoft/applicationinsights-core-js';
import { strNotSpecified } from '../Constants';
var strError = "error";
function _isExceptionDetailsInternal(value) {
  return "hasFullStack" in value && "typeName" in value;
}
function _isExceptionInternal(value) {
  return "ver" in value && "exceptions" in value && "properties" in value;
}
function _getErrorType(errorType) {
  // Gets the Error Type by passing the constructor (used to get the true type of native error object).
  var typeName = "";
  if (errorType) {
    typeName = errorType.typeName || errorType.name || "";
    if (!typeName) {
      try {
        var funcNameRegex = /function (.{1,})\(/;
        var results = funcNameRegex.exec(errorType.constructor.toString());
        typeName = results && results.length > 1 ? results[1] : "";
      } catch (e) {
        // Ignore
      }
    }
  }
  return typeName;
}
var Exception = /** @class */function (_super) {
  __extendsFn(Exception, _super);
  /**
   * Constructs a new instance of the ExceptionTelemetry object
   */
  function Exception(logger, exception, properties, measurements, severityLevel, id) {
    var _this = _super.call(this) || this;
    _this.aiDataContract = {
      ver: FieldType.Required,
      exceptions: FieldType.Required,
      severityLevel: FieldType.Default,
      properties: FieldType.Default,
      measurements: FieldType.Default
    };
    if (!_isExceptionInternal(exception)) {
      _this.exceptions = [new _ExceptionDetails(logger, exception)];
      _this.properties = DataSanitizer.sanitizeProperties(logger, properties) || {};
      _this.measurements = DataSanitizer.sanitizeMeasurements(logger, measurements);
      if (severityLevel) {
        _this.severityLevel = severityLevel;
      }
      if (id) {
        _this.id = id;
      }
    } else {
      _this.exceptions = exception.exceptions;
      _this.properties = exception.properties;
      _this.measurements = exception.measurements;
      if (exception.severityLevel) {
        _this.severityLevel = exception.severityLevel;
      }
      if (exception.id) {
        _this.id = exception.id;
      }
      if (exception.problemGroup) {
        _this.problemGroup = exception.problemGroup;
      }
      // bool/int types, use isNullOrUndefined
      _this.ver = 2; // TODO: handle the CS"4.0" ==> breeze 2 conversion in a better way
      if (!isNullOrUndefined(exception.isManual)) {
        _this.isManual = exception.isManual;
      }
    }
    return _this;
  }
  Exception.CreateFromInterface = function (logger, exception, properties, measurements) {
    var exceptions = exception.exceptions && arrMap(exception.exceptions, function (ex) {
      return _ExceptionDetails.CreateFromInterface(logger, ex);
    });
    var exceptionData = new Exception(logger, __assignFn({}, exception, {
      exceptions: exceptions
    }), properties, measurements);
    return exceptionData;
  };
  Exception.prototype.toInterface = function () {
    var _a = this,
      exceptions = _a.exceptions,
      properties = _a.properties,
      measurements = _a.measurements,
      severityLevel = _a.severityLevel,
      ver = _a.ver,
      problemGroup = _a.problemGroup,
      id = _a.id,
      isManual = _a.isManual;
    var exceptionDetailsInterface = exceptions instanceof Array && arrMap(exceptions, function (exception) {
      return exception.toInterface();
    }) || undefined;
    return {
      ver: "4.0",
      exceptions: exceptionDetailsInterface,
      severityLevel: severityLevel,
      properties: properties,
      measurements: measurements,
      problemGroup: problemGroup,
      id: id,
      isManual: isManual
    };
  };
  /**
   * Creates a simple exception with 1 stack frame. Useful for manual constracting of exception.
   */
  Exception.CreateSimpleException = function (message, typeName, assembly, fileName, details, line) {
    return {
      exceptions: [{
        hasFullStack: true,
        message: message,
        stack: details,
        typeName: typeName
      }]
    };
  };
  Exception.envelopeType = "Microsoft.ApplicationInsights.{0}.Exception";
  Exception.dataType = "ExceptionData";
  return Exception;
}(ExceptionData);
export { Exception };
var _ExceptionDetails = /** @class */function (_super) {
  __extendsFn(_ExceptionDetails, _super);
  function _ExceptionDetails(logger, exception) {
    var _this = _super.call(this) || this;
    _this.aiDataContract = {
      id: FieldType.Default,
      outerId: FieldType.Default,
      typeName: FieldType.Required,
      message: FieldType.Required,
      hasFullStack: FieldType.Default,
      stack: FieldType.Default,
      parsedStack: FieldType.Array
    };
    if (!_isExceptionDetailsInternal(exception)) {
      var error = exception;
      if (!isError(error)) {
        error = error[strError] || error.evt || error;
      }
      _this.typeName = DataSanitizer.sanitizeString(logger, _getErrorType(error)) || strNotSpecified;
      _this.message = DataSanitizer.sanitizeMessage(logger, exception.message) || strNotSpecified;
      var stack = exception.stack;
      _this.parsedStack = _ExceptionDetails.parseStack(stack);
      _this.stack = DataSanitizer.sanitizeException(logger, stack);
      _this.hasFullStack = isArray(_this.parsedStack) && _this.parsedStack.length > 0;
    } else {
      _this.typeName = exception.typeName;
      _this.message = exception.message;
      _this.stack = exception.stack;
      _this.parsedStack = exception.parsedStack;
      _this.hasFullStack = exception.hasFullStack;
    }
    return _this;
  }
  _ExceptionDetails.prototype.toInterface = function () {
    var parsedStack = this.parsedStack instanceof Array && arrMap(this.parsedStack, function (frame) {
      return frame.toInterface();
    });
    var exceptionDetailsInterface = {
      id: this.id,
      outerId: this.outerId,
      typeName: this.typeName,
      message: this.message,
      hasFullStack: this.hasFullStack,
      stack: this.stack,
      parsedStack: parsedStack || undefined
    };
    return exceptionDetailsInterface;
  };
  _ExceptionDetails.CreateFromInterface = function (logger, exception) {
    var parsedStack = exception.parsedStack instanceof Array && arrMap(exception.parsedStack, function (frame) {
      return _StackFrame.CreateFromInterface(frame);
    }) || exception.parsedStack;
    var exceptionDetails = new _ExceptionDetails(logger, __assignFn({}, exception, {
      parsedStack: parsedStack
    }));
    return exceptionDetails;
  };
  _ExceptionDetails.parseStack = function (stack) {
    var parsedStack;
    if (isString(stack)) {
      var frames_1 = stack.split('\n');
      parsedStack = [];
      var level = 0;
      var totalSizeInBytes = 0;
      for (var i = 0; i <= frames_1.length; i++) {
        var frame = frames_1[i];
        if (_StackFrame.regex.test(frame)) {
          var parsedFrame = new _StackFrame(frames_1[i], level++);
          totalSizeInBytes += parsedFrame.sizeInBytes;
          parsedStack.push(parsedFrame);
        }
      }
      // DP Constraint - exception parsed stack must be < 32KB
      // remove frames from the middle to meet the threshold
      var exceptionParsedStackThreshold = 32 * 1024;
      if (totalSizeInBytes > exceptionParsedStackThreshold) {
        var left = 0;
        var right = parsedStack.length - 1;
        var size = 0;
        var acceptedLeft = left;
        var acceptedRight = right;
        while (left < right) {
          // check size
          var lSize = parsedStack[left].sizeInBytes;
          var rSize = parsedStack[right].sizeInBytes;
          size += lSize + rSize;
          if (size > exceptionParsedStackThreshold) {
            // remove extra frames from the middle
            var howMany = acceptedRight - acceptedLeft + 1;
            parsedStack.splice(acceptedLeft, howMany);
            break;
          }
          // update pointers
          acceptedLeft = left;
          acceptedRight = right;
          left++;
          right--;
        }
      }
    }
    return parsedStack;
  };
  return _ExceptionDetails;
}(ExceptionDetails);
export { _ExceptionDetails };
var _StackFrame = /** @class */function (_super) {
  __extendsFn(_StackFrame, _super);
  function _StackFrame(sourceFrame, level) {
    var _this = _super.call(this) || this;
    _this.sizeInBytes = 0;
    _this.aiDataContract = {
      level: FieldType.Required,
      method: FieldType.Required,
      assembly: FieldType.Default,
      fileName: FieldType.Default,
      line: FieldType.Default
    };
    // Not converting this to isString() as typescript uses this logic to "understand" the different
    // types for the 2 different code paths
    if (typeof sourceFrame === "string") {
      var frame = sourceFrame;
      _this.level = level;
      _this.method = "<no_method>";
      _this.assembly = strTrim(frame);
      _this.fileName = "";
      _this.line = 0;
      var matches = frame.match(_StackFrame.regex);
      if (matches && matches.length >= 5) {
        _this.method = strTrim(matches[2]) || _this.method;
        _this.fileName = strTrim(matches[4]);
        _this.line = parseInt(matches[5]) || 0;
      }
    } else {
      _this.level = sourceFrame.level;
      _this.method = sourceFrame.method;
      _this.assembly = sourceFrame.assembly;
      _this.fileName = sourceFrame.fileName;
      _this.line = sourceFrame.line;
      _this.sizeInBytes = 0;
    }
    _this.sizeInBytes += _this.method.length;
    _this.sizeInBytes += _this.fileName.length;
    _this.sizeInBytes += _this.assembly.length;
    // todo: these might need to be removed depending on how the back-end settles on their size calculation
    _this.sizeInBytes += _StackFrame.baseSize;
    _this.sizeInBytes += _this.level.toString().length;
    _this.sizeInBytes += _this.line.toString().length;
    return _this;
  }
  _StackFrame.CreateFromInterface = function (frame) {
    return new _StackFrame(frame, null /* level is available in frame interface */);
  };

  _StackFrame.prototype.toInterface = function () {
    return {
      level: this.level,
      method: this.method,
      assembly: this.assembly,
      fileName: this.fileName,
      line: this.line
    };
  };
  // regex to match stack frames from ie/chrome/ff
  // methodName=$2, fileName=$4, lineNo=$5, column=$6
  _StackFrame.regex = /^([\s]+at)?(.*?)(\@|\s\(|\s)([^\(\@\n]+):([0-9]+):([0-9]+)(\)?)$/;
  _StackFrame.baseSize = 58; // '{"method":"","level":,"assembly":"","fileName":"","line":}'.length
  return _StackFrame;
}(StackFrame);
export { _StackFrame };